import { Type, type Static } from '@sinclair/typebox';
import { SeoBacklinkMode } from '../translation_namespace/seo_content_namespace';
import type { SeoPersona } from './seo-brief.schema';

/**
 * Réglages SEO d'une cible (écran « Réglages » de la page Articles SEO côté pro ; global
 * Payload « Réglages SEO » côté Propelsia) : jours et heure de publication, voix, sujets à
 * cibler ou à éviter, mots interdits, consignes de rédaction. Fusionnés dans le
 * `SeoTargetProfile` par le moteur ; les listes se saisissent une entrée par ligne.
 */
const dayField = (title: string): ReturnType<typeof Type.Boolean> => Type.Boolean({ title });

/** Mode de rédaction des articles partenaires : partagé par les réglages et le parcours d'activation. */
export const SeoBacklinkModeSchema = Type.Union(
  [
    Type.Literal(SeoBacklinkMode.AUTO, { label: 'SEO_BACKLINK_MODE_AUTO' }),
    Type.Literal(SeoBacklinkMode.REVIEW, { label: 'SEO_BACKLINK_MODE_REVIEW' }),
  ],
  { title: 'INPUT_TITLE_SEO_BACKLINK_MODE' },
);

export const SeoSettingsSchema = Type.Object({
  publish_monday: dayField('INPUT_TITLE_SEO_PUBLISH_MONDAY'),
  publish_tuesday: dayField('INPUT_TITLE_SEO_PUBLISH_TUESDAY'),
  publish_wednesday: dayField('INPUT_TITLE_SEO_PUBLISH_WEDNESDAY'),
  publish_thursday: dayField('INPUT_TITLE_SEO_PUBLISH_THURSDAY'),
  publish_friday: dayField('INPUT_TITLE_SEO_PUBLISH_FRIDAY'),
  publish_saturday: dayField('INPUT_TITLE_SEO_PUBLISH_SATURDAY'),
  publish_sunday: dayField('INPUT_TITLE_SEO_PUBLISH_SUNDAY'),
  publish_hour: Type.Integer({ minimum: 0, maximum: 23, title: 'INPUT_TITLE_SEO_PUBLISH_HOUR' }),
  /**
   * Membre de l'équipe qui signe les articles (2026-10-12) : ils parlent alors en « je » et le
   * persona se construit sur cette personne. Absent = l'établissement signe, les articles parlent
   * en « nous ». Remplace l'ancien choix « je / nous » (`write_as`, ignoré s'il reste en base).
   */
  author_team_member_id: Type.Optional(Type.Integer({ minimum: 1, title: 'INPUT_TITLE_SEO_AUTHOR' })),
  /**
   * Articles publiés sur les sites partenaires (backlinks) : rédaction automatique ou relecture
   * par le pro avant envoi. Absent tant que le pro n'a pas choisi (onglet Backlinks) ; AUTO vaut
   * acceptation de l'article figé une fois publié.
   */
  backlink_mode: Type.Optional(SeoBacklinkModeSchema),
  tone: Type.Optional(
    Type.String({ maxLength: 200, title: 'INPUT_TITLE_SEO_TONE', placeholder: 'INPUT_PLACEHOLDER_SEO_TONE' }),
  ),
  /** Sujets, prestations ou thèmes à cibler en priorité (un par ligne). */
  focus_topics: Type.Optional(
    Type.String({
      maxLength: 1500,
      title: 'INPUT_TITLE_SEO_FOCUS_TOPICS',
      placeholder: 'INPUT_PLACEHOLDER_SEO_FOCUS_TOPICS',
      meta: { multiline: true },
    }),
  ),
  /** Sujets à ne jamais proposer (un par ligne). */
  excluded_topics: Type.Optional(
    Type.String({
      maxLength: 1500,
      title: 'INPUT_TITLE_SEO_EXCLUDED_TOPICS',
      placeholder: 'INPUT_PLACEHOLDER_SEO_EXCLUDED_TOPICS',
      meta: { multiline: true },
    }),
  ),
  /** Mots ou marques à ne jamais écrire (un par ligne). */
  forbidden_terms: Type.Optional(
    Type.String({
      maxLength: 1000,
      title: 'INPUT_TITLE_SEO_FORBIDDEN_TERMS',
      placeholder: 'INPUT_PLACEHOLDER_SEO_FORBIDDEN_TERMS',
      meta: { multiline: true },
    }),
  ),
  /**
   * Sites concurrents à surveiller, un par ligne (URL ou domaine). Leurs sujets nourrissent la
   * découverte de mots-clés (source `COMPETITOR`) ; ils ne sont jamais cités dans un article.
   */
  competitor_sites: Type.Optional(
    Type.String({
      maxLength: 1000,
      title: 'INPUT_TITLE_SEO_COMPETITOR_SITES',
      placeholder: 'INPUT_PLACEHOLDER_SEO_COMPETITOR_SITES',
      meta: { multiline: true },
    }),
  ),
  /** Consignes libres de rédaction (ce qu'on dit, ce qu'on ne dit pas, tutoiement…). */
  writing_guidelines: Type.Optional(
    Type.String({
      maxLength: 2000,
      title: 'INPUT_TITLE_SEO_WRITING_GUIDELINES',
      placeholder: 'INPUT_PLACEHOLDER_SEO_WRITING_GUIDELINES',
      meta: { multiline: true },
    }),
  ),
  /**
   * Persona éditorial (2026-09-06) : généré une fois par le moteur depuis les données du pro
   * (`generatePersona`) quand ces champs sont vides, puis relu et corrigé ici. Vider `persona_who`
   * = demander une nouvelle génération au prochain cycle.
   */
  persona_who: Type.Optional(
    Type.String({
      maxLength: 200,
      title: 'INPUT_TITLE_SEO_PERSONA_WHO',
      placeholder: 'INPUT_PLACEHOLDER_SEO_PERSONA_WHO',
    }),
  ),
  /** Partis pris métier, un par ligne (opinions, jamais de chiffre). */
  persona_convictions: Type.Optional(
    Type.String({
      maxLength: 1000,
      title: 'INPUT_TITLE_SEO_PERSONA_CONVICTIONS',
      placeholder: 'INPUT_PLACEHOLDER_SEO_PERSONA_CONVICTIONS',
      meta: { multiline: true },
    }),
  ),
  /** Idées reçues corrigées souvent, une par ligne. */
  persona_misconceptions: Type.Optional(
    Type.String({
      maxLength: 1000,
      title: 'INPUT_TITLE_SEO_PERSONA_MISCONCEPTIONS',
      placeholder: 'INPUT_PLACEHOLDER_SEO_PERSONA_MISCONCEPTIONS',
      meta: { multiline: true },
    }),
  ),
  /** Ce que l'auteur ne dit jamais, un par ligne. */
  persona_never_says: Type.Optional(
    Type.String({
      maxLength: 1000,
      title: 'INPUT_TITLE_SEO_PERSONA_NEVER_SAYS',
      placeholder: 'INPUT_PLACEHOLDER_SEO_PERSONA_NEVER_SAYS',
      meta: { multiline: true },
    }),
  ),
});
export type SeoSettingsObject = Static<typeof SeoSettingsSchema>;

export const SEO_SETTINGS_DEFAULTS: SeoSettingsObject = {
  publish_monday: true,
  publish_tuesday: false,
  publish_wednesday: true,
  publish_thursday: false,
  publish_friday: false,
  publish_saturday: false,
  publish_sunday: false,
  publish_hour: 8,
};

/** Pronom des articles : « je » quand un membre signe, « nous » quand l'établissement signe. */
export function seoSettingsWriteAs(settings: Pick<SeoSettingsObject, 'author_team_member_id'>): 'je' | 'nous' {
  return settings.author_team_member_id ? 'je' : 'nous';
}

/** Champs du persona : remis à zéro quand l'auteur change (il est régénéré pour la nouvelle voix). */
export const SEO_PERSONA_SETTING_KEYS = [
  'persona_who',
  'persona_convictions',
  'persona_misconceptions',
  'persona_never_says',
] as const satisfies readonly (keyof SeoSettingsObject)[];

/** Champ booléen des réglages pour chaque jour ISO (1 = lundi … 7 = dimanche). */
export const SEO_PUBLISH_DAY_FIELD_BY_ISO: Record<number, keyof SeoSettingsObject> = {
  1: 'publish_monday',
  2: 'publish_tuesday',
  3: 'publish_wednesday',
  4: 'publish_thursday',
  5: 'publish_friday',
  6: 'publish_saturday',
  7: 'publish_sunday',
};

/** Les sept booléens des réglages à partir d'une liste de jours ISO (les autres jours à `false`). */
export function seoSettingsFromPublishDays(
  days: readonly number[],
): Pick<
  SeoSettingsObject,
  | 'publish_monday'
  | 'publish_tuesday'
  | 'publish_wednesday'
  | 'publish_thursday'
  | 'publish_friday'
  | 'publish_saturday'
  | 'publish_sunday'
> {
  return {
    publish_monday: days.includes(1),
    publish_tuesday: days.includes(2),
    publish_wednesday: days.includes(3),
    publish_thursday: days.includes(4),
    publish_friday: days.includes(5),
    publish_saturday: days.includes(6),
    publish_sunday: days.includes(7),
  };
}

/**
 * Sélection de jours bornée (réglages et parcours d'activation) : `selected` est dans l'ordre où
 * les jours ont été choisis (le plus ancien d'abord). Décocher retire le jour ; cocher au-delà de
 * `max` remplace le plus ancien plutôt que d'ignorer le clic.
 */
export function togglePublishDay(selected: readonly number[], day: number, max: number): number[] {
  if (selected.includes(day)) return selected.filter((d): boolean => d !== day);
  const kept = selected.length >= max ? selected.slice(Math.max(0, selected.length - max + 1)) : [...selected];
  return [...kept, day];
}

/** Jours ISO (1 = lundi … 7 = dimanche) cochés ; lundi + mercredi si rien n'est coché. */
export function seoSettingsPublishDays(settings: SeoSettingsObject): number[] {
  const days = [
    settings.publish_monday ? 1 : 0,
    settings.publish_tuesday ? 2 : 0,
    settings.publish_wednesday ? 3 : 0,
    settings.publish_thursday ? 4 : 0,
    settings.publish_friday ? 5 : 0,
    settings.publish_saturday ? 6 : 0,
    settings.publish_sunday ? 7 : 0,
  ].filter((d): boolean => d > 0);
  return days.length > 0 ? days : [1, 3];
}

/** Puce de liste en tête de ligne (« - », « • », « * »). */
const LIST_BULLET = /^[-•*]\s*/;
/** Une lettre latine (accents compris) : un « mot » en contient au moins une (« 3 », « - » n'en sont pas). */
const LETTER = /[A-Za-zÀ-ɏḀ-ỿ]/;

/** Ligne d'une liste saisie sans sa puce ni ses espaces (« - balayage » → « balayage »). */
export function stripSeoListBullet(line: string): string {
  return line.trim().replace(LIST_BULLET, '').trim();
}

/** Entrée nettoyée (puce retirée) ; ajoutée si elle a au moins deux caractères et n'y est pas déjà (casse ignorée). */
function pushListEntry(out: string[], raw: string): void {
  const entry = stripSeoListBullet(raw);
  if (entry.length > 1 && !out.some((e): boolean => e.toLowerCase() === entry.toLowerCase())) out.push(entry);
}

/** Découpage brut d'une liste saisie (sans détection de consigne) : `separator` sépare les entrées. */
function splitListEntries(text: string | undefined, separator: RegExp): string[] {
  if (!text) return [];
  const out: string[] = [];
  for (const raw of text.split(separator)) pushListEntry(out, raw);
  return out;
}

/**
 * Au-delà de ce nombre de mots, une ligne d'un champ « liste » est une phrase (consigne), pas un
 * sujet. Calé sur des saisies réelles : un sujet à puce tient en 7 mots (« Préparation émotionnelle
 * et bien-être avant une cœlioscopie »), un interdit éditorial en demande 9 et plus.
 */
export const SEO_LIST_ITEM_MAX_WORDS = 7;

/**
 * Abréviations courantes suivies d'un point qui ne terminent pas une phrase (« Dr. Pierre Ricaud »,
 * « St. Tropez », « etc. ») : une marque ou un nom propre saisi dans les mots interdits reste un mot
 * à filtrer, pas une consigne. Une initiale seule (« J. Dupont ») est traitée de même.
 */
const ABBREVIATIONS = new Set([
  'dr',
  'mr',
  'mrs',
  'ms',
  'm',
  'me',
  'mme',
  'mlle',
  'st',
  'ste',
  'etc',
  'cf',
  'ex',
  'env',
  'av',
  'bd',
  'tel',
  'tél',
  'ref',
  'réf',
  'nb',
  'vs',
  'p',
  'pp',
  'no',
  'n°',
]);

/**
 * Virgule ou point-virgule suivi d'un mot-outil (conjonction, préposition, pronom) : la ponctuation
 * est celle d'une phrase (« …une maladie, même à base de plantes, de compléments… »), pas d'une
 * énumération de sujets (« balayage, lissage brésilien, coupe homme »).
 */
const CLAUSE_OPENER =
  /^(?:[dln][’']|(?:et|ou|ni|mais|donc|car|puis|même|sans|avec|pour|par|sur|sous|dans|en|à|au|aux|de|des|du|que|qui|dont|où|si|comme|sauf|hors|contre|selon|afin|lorsque|quand|tandis|ainsi|aussi|surtout|notamment|voire|y compris|il|elle|ils|elles|on|nous|vous|ne)(?=\s|$))/i;

/** Nombre de mots (un mot contient au moins une lettre : « 3 », « - » n'en sont pas). */
function wordCount(text: string): number {
  return text.split(/\s+/).filter((w): boolean => LETTER.test(w)).length;
}

/**
 * Ponctuation de phrase suivie d'un mot (« … traitement. Ne pas … »). Le point d'une abréviation ou
 * d'une initiale (« Dr. Pierre », « J. Dupont ») ne compte pas ; un point final seul non plus.
 */
function hasSentenceBreak(text: string): boolean {
  if (/[!?…]\s+\S/.test(text)) return true;
  for (const m of text.matchAll(/(\S*)\.\s+\S/g)) {
    const before = m[1].replace(/[^A-Za-zÀ-ɏḀ-ỿ°]/g, '').toLowerCase();
    if (before.length <= 1 || ABBREVIATIONS.has(before)) continue;
    return true;
  }
  return false;
}

/**
 * Une ligne d'un champ « liste » (sujets à cibler, sujets exclus, mots interdits) est une CONSIGNE
 * de rédaction, pas un sujet, si elle ressemble à une phrase : deux-points (hors « :// » d'une URL
 * et hors heure « 14:30 »), ponctuation de phrase suivie d'un mot (`hasSentenceBreak`), ou plus de
 * `SEO_LIST_ITEM_MAX_WORDS` mots. Une énumération de sujets courts séparés par des virgules
 * (« balayage, lissage brésilien, coiffure de mariée, coupe homme, coloration végétale ») se juge
 * sujet par sujet : c'est le plus long qui compte, pas la ligne entière ; une virgule suivie d'un
 * mot-outil (`CLAUSE_OPENER`) rend à la ligne son statut de phrase. Un point final seul est toléré
 * (« Vécu du cancer du sein. » reste un sujet). Déterministe : la même règle sert au moteur et à
 * l'avertissement de saisie.
 */
export function isSeoInstructionLine(line: string): boolean {
  const text = stripSeoListBullet(line);
  if (text.length === 0) return false;
  if (/:(?!\/\/)/.test(text.replace(/\d+:\d+/g, ''))) return true;
  if (hasSentenceBreak(text)) return true;
  const segments = text
    .split(/[,;]+/)
    .map((seg): string => seg.trim())
    .filter((seg): boolean => seg.length > 0);
  const isEnumeration = segments.length > 1 && segments.every((seg): boolean => !CLAUSE_OPENER.test(seg));
  const words = isEnumeration ? Math.max(...segments.map(wordCount)) : wordCount(text);
  return words > SEO_LIST_ITEM_MAX_WORDS;
}

export interface SeoSettingsEntries {
  /** Sujets ou mots courts : seeds, filtres par sous-chaîne, termes interdits (point final retiré). */
  items: string[];
  /** Lignes-phrases, gardées entières (pas de découpe sur les virgules) : consignes de rédaction. */
  instructions: string[];
}

/**
 * Champ « liste » saisi une entrée par ligne → sujets courts d'un côté, consignes de l'autre
 * (`isSeoInstructionLine`). Une ligne courte garde la découpe sur `,` et `;` (« Endométriose, SOPK
 * et règles douloureuses » donne deux seeds exploitables en autocomplétion, là où la ligne entière
 * n'en ferait aucun) ; une ligne-consigne n'est jamais découpée. Dédoublonnage insensible à la casse.
 */
export function seoSettingsEntries(text: string | undefined): SeoSettingsEntries {
  const items: string[] = [];
  const instructions: string[] = [];
  if (!text) return { items, instructions };
  for (const rawLine of text.split(/\n+/)) {
    const line = stripSeoListBullet(rawLine);
    if (line.length === 0) continue;
    if (isSeoInstructionLine(line)) {
      pushListEntry(instructions, line);
      continue;
    }
    for (const raw of line.split(/[,;]+/)) pushListEntry(items, raw.replace(/\.+\s*$/, ''));
  }
  return { items, instructions };
}

/** Champs « liste » des réglages dont les lignes-phrases deviennent des consignes de rédaction. */
export type SeoListSettingsField = 'focus_topics' | 'excluded_topics' | 'forbidden_terms';

/**
 * Préfixe d'une consigne saisie dans un champ « liste » : dit au brief et au rédacteur de quel
 * champ elle vient. Une seule définition pour les deux cibles (pros via le backend, Propelsia via
 * Payload), pour que la même consigne arrive libellée pareil.
 */
export const SEO_LIST_INSTRUCTION_PREFIX: Readonly<Record<SeoListSettingsField, string>> = {
  focus_topics: 'Consigne d’angle',
  excluded_topics: 'À ne jamais proposer ni écrire',
  forbidden_terms: 'À ne jamais écrire',
};

/** Consignes d'un champ « liste » (`seoSettingsEntries().instructions`), préfixées pour `editorial_notes`. */
export function seoListInstructionNotes(field: SeoListSettingsField, instructions: readonly string[]): string[] {
  return instructions.map((instruction): string => `${SEO_LIST_INSTRUCTION_PREFIX[field]} : ${instruction}`);
}

/**
 * Liste saisie « une entrée par ligne » (virgules tolérées) → sujets courts nettoyés, dédoublonnés.
 * Les lignes-consignes en sont retirées : `seoSettingsEntries(text).instructions` les donne.
 */
export function seoSettingsList(text: string | undefined): string[] {
  return seoSettingsEntries(text).items;
}

/**
 * Site canonique d'une saisie libre : « https://www.salon-x.fr/blog/ » → « salon-x.fr/blog »,
 * « salon-x.fr » → « salon-x.fr » (minuscules, sans schéma, sans `www.`, sans slash final ni
 * paramètres). Le chemin, s'il y en a un, dit quelle section lire. `null` si ce n'est pas un domaine.
 */
export function siteFromInput(raw: string): string | null {
  const trimmed = raw.trim().replace(/^[-•*]\s*/, '');
  if (trimmed.length === 0) return null;
  try {
    const url = new URL(/^[a-z]+:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
    const host = url.hostname.toLowerCase().replace(/^www\./, '');
    if (!/^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(host)) return null;
    const path = url.pathname.replace(/\/+$/, '');
    return path.length > 1 ? `${host}${path}` : host;
  } catch {
    return null;
  }
}

/** Hôte d'un site canonique (« fresha.com/fr/blog » → « fresha.com »). */
export function siteHost(site: string): string {
  return site.split('/')[0];
}

/** Liste « un site par ligne » (virgules tolérées) → sites canoniques dédoublonnés (une URL n'est jamais une consigne). */
export function seoSettingsSites(text: string | undefined): string[] {
  const out: string[] = [];
  for (const entry of splitListEntries(text, /[\n,;]+/)) {
    const site = siteFromInput(entry);
    if (site && !out.includes(site)) out.push(site);
  }
  return out;
}

/** Réglages effectifs : défauts complétés par ce qui est enregistré (tolère un JSON partiel). */
export function mergeSeoSettings(stored: Partial<SeoSettingsObject> | null | undefined): SeoSettingsObject {
  return { ...SEO_SETTINGS_DEFAULTS, ...(stored ?? {}) };
}

/** Liste « une phrase par ligne » : les virgules font partie de la phrase (contrairement à `seoSettingsList`). */
export function seoSettingsLines(text: string | undefined): string[] {
  return splitListEntries(text, /\n+/);
}

/** Persona des réglages, ou `undefined` tant que « qui parle » n'est pas renseigné. */
export function personaFromSettings(settings: Partial<SeoSettingsObject>): SeoPersona | undefined {
  const who = settings.persona_who?.trim() ?? '';
  if (who.length === 0) return undefined;
  return {
    who,
    convictions: seoSettingsLines(settings.persona_convictions),
    misconceptions: seoSettingsLines(settings.persona_misconceptions),
    never_says: seoSettingsLines(settings.persona_never_says),
  };
}

/** Champs de réglages correspondant à un persona (une entrée par ligne). */
export function personaToSettings(
  persona: SeoPersona,
): Pick<SeoSettingsObject, 'persona_who' | 'persona_convictions' | 'persona_misconceptions' | 'persona_never_says'> {
  return {
    persona_who: persona.who,
    persona_convictions: persona.convictions.join('\n'),
    persona_misconceptions: persona.misconceptions.join('\n'),
    persona_never_says: persona.never_says.join('\n'),
  };
}
